import debounce from "../../utils/debounce";

const DESKTOP_ITEMS_TO_SHOW: number = 4;
const MOBILE_ITEMS_TO_SHOW: number = 2;
const DEFAULT_DEBOUNCE_TIME: number = 300;
const MAX_MOBILE_WIDTH: number = 992;

class PredictiveSearch extends HTMLElement {
  private input: HTMLInputElement;
  private predictiveSearchResults: HTMLElement;
  private predictiveSearchProducts: HTMLElement[];
  private predictiveSearchProductTypes: NodeListOf<HTMLElement>;
  private itemsToShow: number;
  private selectedProductType: string;
  private handleResize: (event: Event) => void;
  private handleInput: (event: Event) => void;
  private searchModalNew: HTMLElement;
  private handleProductTypeSelect: (event: Event) => void;
  private productBlockCleanupFunctions: Array<() => void> = [];

  constructor() {
    super();

    this.input = this.querySelector('input[name="q"]');
    this.predictiveSearchResults = document.querySelector("#predictive-search");
    this.searchModalNew = document.querySelector("#search-modal-new");
    this.predictiveSearchProducts = [];

    this.handleProductTypeSelect = this.onProductTypeSelect.bind(this);
    this.handleResize = debounce(this.onResize.bind(this), 50);
    this.handleInput = debounce(
      this.onChange.bind(this),
      DEFAULT_DEBOUNCE_TIME,
    );

    this.input.addEventListener("input", this.handleInput);
    window.addEventListener("resize", this.handleResize);

    this.onResize();

    ["click", "touchstart", "touchend", "mousedown"].forEach((evt) => {
      this.addEventListener(
        evt,
        (e) => {
          e.stopPropagation();
        },
        true,
      );
    });
  }

  onResize() {
    if (window.innerWidth <= MAX_MOBILE_WIDTH) {
      this.itemsToShow = MOBILE_ITEMS_TO_SHOW;
    } else {
      this.itemsToShow = DESKTOP_ITEMS_TO_SHOW;
    }

    this.onChangeVisibility();
  }

  onChangeVisibility() {
    if (!this.predictiveSearchProducts?.length) return;

    let index = 0;

    this.predictiveSearchProducts.forEach((product: HTMLElement) => {
      product.style.display = "block";
    });

    this.predictiveSearchProducts.forEach((product: HTMLElement) => {
      if (index === this.itemsToShow) {
        product.style.display = "none";
        return;
      }

      if (
        this.selectedProductType === undefined ||
        product.dataset.productType === this.selectedProductType
      ) {
        product.style.display = "block";
        index++;
      } else {
        product.style.display = "none";
      }
    });
  }

  onProductTypeSelect(event: Event) {
    event.stopPropagation();
    event.preventDefault();

    this.predictiveSearchProductTypes?.forEach(
      (productTypeElem: HTMLElement) => {
        productTypeElem.classList.remove("active");
      },
    );

    const element = event.currentTarget as HTMLElement;
    element.classList.add("active");

    this.selectedProductType = element.dataset.productTypeSelect;

    this.onChangeVisibility();
  }

  async onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      return;
    }

    await this.getSearchResults(searchTerm);

    this.predictiveSearchProductTypes = document.querySelectorAll(
      "[data-product-type-select]",
    );

    const searchProducts = document.querySelectorAll<HTMLElement>(
      "#predictive-search-products [data-product-type]",
    );

    for (let i = this.itemsToShow; i < searchProducts.length; i++) {
      searchProducts[i].style.display = "none";
    }

    this.predictiveSearchProducts = Array.from(searchProducts);
    this.predictiveSearchProductTypes.forEach((productType) => {
      productType.addEventListener("click", this.handleProductTypeSelect);
    });
  }

  getSearchResults(searchTerm: string): Promise<void> {
    const urlParams = new URLSearchParams({
      q: searchTerm,
      section_id: "predictive-search",
      "resources[type]": "product",
    });

    return fetch(`/search/suggest?${urlParams.toString()}`)
      .then((response) => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status.toString());
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.initializeProductBlocks();
        this.open();
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  open() {
    this.predictiveSearchResults.style.display = "block";
  }

  initializeProductBlocks() {
    // Clean up previous event listeners to prevent memory leaks
    this.cleanupProductBlocks();

    if (!this.predictiveSearchResults) return;

    const jq = (window as any).jQuery;
    const theme = (window as any).theme;

    // Initialize inline videos via theme.VideoManager so <video> elements are created
    if (theme?.inlineVideos?.init) {
      theme.inlineVideos.init(this.predictiveSearchResults);
    }

    // Trigger cc:component:load for product-block so ProductBlockInstance is created
    // (handles hover image slider, etc.)
    if (jq) {
      jq(document).trigger("cc:component:load", [
        "product-block",
        this.predictiveSearchResults,
      ]);
    }

    // Initialize variant viewport switcher for any blocks not handled by theme.js
    const productBlocks = this.predictiveSearchResults.querySelectorAll(
      ".cc-product-block:not(.cc-initialized)",
    );

    productBlocks.forEach((block) => {
      block.classList.add("cc-initialized");
      this.initializeVariantSwitcher(block as HTMLElement);
    });
  }

  private cleanupProductBlocks() {
    // Destroy inline videos from previous results
    const theme = (window as any).theme;
    if (theme?.inlineVideos?.destroy && this.predictiveSearchResults) {
      theme.inlineVideos.destroy(this.predictiveSearchResults);
    }

    // Remove all event listeners from previous search results
    this.productBlockCleanupFunctions.forEach((cleanup) => cleanup());
    this.productBlockCleanupFunctions = [];
  }

  private initializeVariantSwitcher(productBlock: HTMLElement) {
    const variantViewports = productBlock.querySelectorAll(
      ".product-block__viewport",
    );
    const collectionSwatches = productBlock.querySelectorAll(
      ".swatches--collection a",
    );

    if (variantViewports.length > 0 && collectionSwatches.length > 0) {
      let activeViewportKey: string | null = null;

      // Find the initially active viewport
      variantViewports.forEach((viewport) => {
        if (
          viewport.getAttribute("data-active") === "true" &&
          viewport.hasAttribute("data-product-key")
        ) {
          activeViewportKey = viewport.getAttribute("data-product-key");
        }
      });

      const eventListeners: Array<{
        element: Element;
        handler: (e: Event) => void;
      }> = [];

      // Add click handlers to swatches
      collectionSwatches.forEach((swatch) => {
        const swatchElement = swatch as HTMLElement;
        const clickHandler = (e: Event) => {
          e.preventDefault();

          const variantKey =
            swatchElement.getAttribute("data-variant-key") ||
            swatchElement.getAttribute("data-product-key");

          if (variantKey && variantKey !== activeViewportKey) {
            // Update active viewport
            variantViewports.forEach((viewport) => {
              const viewportElement = viewport as HTMLElement;
              const viewportKey =
                viewportElement.getAttribute("data-product-key");

              if (viewportKey === variantKey) {
                viewportElement.setAttribute("data-active", "true");
              } else {
                viewportElement.setAttribute("data-active", "false");
              }
            });

            activeViewportKey = variantKey;
          }
        };

        swatchElement.addEventListener("click", clickHandler);
        eventListeners.push({ element: swatchElement, handler: clickHandler });
      });

      // Store cleanup function
      this.productBlockCleanupFunctions.push(() => {
        eventListeners.forEach(({ element, handler }) => {
          element.removeEventListener("click", handler);
        });
      });
    }
  }

  close() {
    this.searchModalNew.classList.remove("reveal");
    this.predictiveSearchResults.style.display = "none";
    this.input.value = "";

    this.predictiveSearchProductTypes?.forEach((productType) => {
      productType.removeEventListener("click", this.handleProductTypeSelect);
    });

    this.predictiveSearchProductTypes = null;
    this.predictiveSearchProducts = null;

    // Clean up product block event listeners
    this.cleanupProductBlocks();

    document.body.dataset.modal = "";
    this.selectedProductType = "";
  }

  disconnectedCallback() {
    if (this.input && this.handleInput) {
      this.input.removeEventListener("input", this.handleInput);
    }
    if (this.handleResize) {
      window.removeEventListener("resize", this.handleResize);
    }
  }
}

if (!window.customElements.get("predictive-search")) {
  window.customElements.define("predictive-search", PredictiveSearch);
}

export {};
